// src/pages/corrales/CorralCouponsAdminPage.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  corralCouponsAdminApi,
  CorralCouponAdminItem,
  CorralCouponStatus,
} from "../../api/corralCouponsAdminApi";

const STATUS_LABEL: Record<CorralCouponStatus, string> = {
  PENDING: "Pendiente de canjear",
  REDEEMED: "Canjeado",
  EXPIRED: "Vencido",
  REVOKED: "Revocado",
};

function StatusBadge({ status }: { status: CorralCouponStatus }) {
  const color =
    status === "REDEEMED"
      ? "#0cbb1d"
      : status === "PENDING"
        ? "#f98115"
        : status === "REVOKED"
          ? "#D60D4C"
          : "#8A8F98";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: `${color}1F`,
        color,
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function formatDate(iso: string | null) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const PAGE_SIZE = 20;

export default function CorralCouponsAdminPage() {
  const [coupons, setCoupons] = useState<CorralCouponAdminItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<CorralCouponStatus | "">("");
  const [loading, setLoading] = useState(true);

  const [target, setTarget] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unlimited, setUnlimited] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const showMsg = (msg: string, isError = false) => {
    if (isError) {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(""), 5000);
    } else {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(""), 5000);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await corralCouponsAdminApi.listar(page, PAGE_SIZE, statusFilter);
      setCoupons(res.data?.content ?? []);
      setTotalPages(res.data?.totalPages ?? 0);
    } catch {
      setCoupons([]);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target.trim()) {
      showMsg("Indica el email o el ID del usuario destinatario", true);
      return;
    }
    if (!expiresAt) {
      showMsg("Indica la fecha de expiración del cupón", true);
      return;
    }
    if (!unlimited && (!quantity || Number(quantity) < 1 || Number(quantity) > 10)) {
      showMsg("La cantidad de Corrales debe estar entre 1 y 10 (o marca 'ilimitado')", true);
      return;
    }

    setCreating(true);
    try {
      const isEmail = target.includes("@");
      const res = await corralCouponsAdminApi.crear({
        targetUserId: isEmail ? undefined : target.trim(),
        targetUserEmail: isEmail ? target.trim() : undefined,
        quantity: unlimited ? undefined : Number(quantity),
        unlimited,
        expiresAt: new Date(expiresAt).toISOString(),
      });
      const code = res.data?.content?.code;
      showMsg(`✓ Cupón ${code ?? ""} creado para ${target.trim()}`);
      setTarget("");
      setQuantity("1");
      setUnlimited(false);
      setExpiresAt("");
      setPage(0);
      await load();
    } catch (e: any) {
      showMsg(e?.response?.data?.message || "Error al crear el cupón", true);
    } finally {
      setCreating(false);
    }
  };

  const onRevoke = async (id: string, code: string) => {
    if (!window.confirm(`¿Revocar el cupón ${code}? Ya no podrá canjearse.`)) return;
    setRevokingId(id);
    try {
      await corralCouponsAdminApi.revocar(id);
      showMsg(`✓ Cupón ${code} revocado`);
      await load();
    } catch (e: any) {
      showMsg(e?.response?.data?.message || "Error al revocar el cupón", true);
    } finally {
      setRevokingId(null);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 4 }}>Cupones de Corral</h2>
      <p style={{ color: "#6b7280", marginBottom: 20, fontSize: "0.9rem" }}>
        Regala publicaciones gratis a un usuario específico. Cada cupón se asigna a un solo
        usuario, solo se puede canjear una vez, y vence en la fecha que definas aquí.
      </p>

      {successMsg && (
        <div style={{ background: "#0cbb1d1F", color: "#0cbb1d", padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: "0.85rem" }}>
          {successMsg}
        </div>
      )}
      {errorMsg && (
        <div style={{ background: "#D60D4C1F", color: "#D60D4C", padding: "10px 14px", borderRadius: 8, marginBottom: 14, fontSize: "0.85rem" }}>
          {errorMsg}
        </div>
      )}

      <form
        onSubmit={onCreate}
        style={{
          background: "#fff",
          border: "1px solid #eee",
          borderRadius: 12,
          padding: 20,
          marginBottom: 28,
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 240px" }}>
          <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
            Usuario destinatario (email o ID)
          </label>
          <input
            type="text"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="usuario@correo.com"
            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd" }}
          />
        </div>

        <div style={{ flex: "0 1 120px" }}>
          <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
            Cantidad de Corrales
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={quantity}
            disabled={unlimited}
            onChange={(e) => setQuantity(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd", opacity: unlimited ? 0.5 : 1 }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 6, paddingBottom: 8 }}>
          <input
            id="coupon-unlimited"
            type="checkbox"
            checked={unlimited}
            onChange={(e) => setUnlimited(e.target.checked)}
          />
          <label htmlFor="coupon-unlimited" style={{ fontSize: "0.82rem" }}>
            Plan ilimitado
          </label>
        </div>

        <div style={{ flex: "0 1 200px" }}>
          <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, marginBottom: 4 }}>
            Vence el
          </label>
          <input
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #ddd" }}
          />
        </div>

        <button
          type="submit"
          disabled={creating}
          style={{
            padding: "10px 20px",
            borderRadius: 8,
            border: "none",
            background: "#1c6b3e",
            color: "#fff",
            fontWeight: 700,
            cursor: creating ? "default" : "pointer",
            opacity: creating ? 0.7 : 1,
          }}
        >
          {creating ? "Creando…" : "Crear cupón"}
        </button>
      </form>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <label style={{ fontSize: "0.82rem", fontWeight: 600 }}>Filtrar por estado:</label>
        <select
          value={statusFilter}
          onChange={(e) => {
            setPage(0);
            setStatusFilter(e.target.value as CorralCouponStatus | "");
          }}
          style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #ddd" }}
        >
          <option value="">Todos</option>
          <option value="PENDING">Pendientes</option>
          <option value="REDEEMED">Canjeados</option>
          <option value="EXPIRED">Vencidos</option>
          <option value="REVOKED">Revocados</option>
        </select>
      </div>

      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ background: "#fafafa", textAlign: "left" }}>
              <th style={{ padding: "10px 14px" }}>Código</th>
              <th style={{ padding: "10px 14px" }}>Usuario</th>
              <th style={{ padding: "10px 14px" }}>Corrales</th>
              <th style={{ padding: "10px 14px" }}>Estado</th>
              <th style={{ padding: "10px 14px" }}>Registrado</th>
              <th style={{ padding: "10px 14px" }}>Vence</th>
              <th style={{ padding: "10px 14px" }}>Canjeado</th>
              <th style={{ padding: "10px 14px" }}>Creado por</th>
              <th style={{ padding: "10px 14px" }} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, textAlign: "center", color: "#999" }}>
                  Cargando…
                </td>
              </tr>
            ) : coupons.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: 20, textAlign: "center", color: "#999" }}>
                  No hay cupones para mostrar
                </td>
              </tr>
            ) : (
              coupons.map((c) => (
                <tr key={c.id} style={{ borderTop: "1px solid #f0f0f0" }}>
                  <td style={{ padding: "10px 14px", fontFamily: "monospace" }}>{c.code}</td>
                  <td style={{ padding: "10px 14px" }}>
                    <div>{c.targetUserFullName}</div>
                    <div style={{ color: "#999", fontSize: "0.76rem" }}>{c.targetUserEmail}</div>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    {c.unlimited ? "Ilimitado" : c.quantity}
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <StatusBadge status={c.status} />
                  </td>
                  <td style={{ padding: "10px 14px" }}>{formatDate(c.createdAt)}</td>
                  <td style={{ padding: "10px 14px" }}>{formatDate(c.expiresAt)}</td>
                  <td style={{ padding: "10px 14px" }}>{formatDate(c.redeemedAt)}</td>
                  <td style={{ padding: "10px 14px" }}>{c.createdByAdminName ?? "—"}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right" }}>
                    {c.status === "PENDING" && (
                      <button
                        onClick={() => onRevoke(c.id, c.code)}
                        disabled={revokingId === c.id}
                        style={{
                          padding: "5px 12px",
                          borderRadius: 6,
                          border: "1px solid #D60D4C",
                          background: "#fff",
                          color: "#D60D4C",
                          fontSize: "0.78rem",
                          fontWeight: 600,
                          cursor: revokingId === c.id ? "default" : "pointer",
                          opacity: revokingId === c.id ? 0.6 : 1,
                        }}
                      >
                        {revokingId === c.id ? "Revocando…" : "Revocar"}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 16 }}>
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", cursor: page === 0 ? "default" : "pointer" }}
          >
            Anterior
          </button>
          <span style={{ fontSize: "0.82rem", alignSelf: "center" }}>
            Página {page + 1} de {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid #ddd", background: "#fff", cursor: page >= totalPages - 1 ? "default" : "pointer" }}
          >
            Siguiente
          </button>
        </div>
      )}
    </div>
  );
}
